import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const PremiumScrollEffects = () => {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // IRIS 💎 PREMIUM SCROLL EFFECTS
    
    // 1. Smooth scroll-jacking timeline
    let sections = gsap.utils.toArray('.premium-scroll-section');
    
    sections.forEach((section, index) => {
      ScrollTrigger.create({
        trigger: section,
        start: "top bottom",
        end: "bottom top",
        onEnter: () => {
          gsap.from(section.querySelectorAll('.reveal-on-scroll'), {
            y: 60,
            opacity: 0,
            duration: 1.2,
            stagger: 0.15,
            ease: "power3.out"
          });
        }
      });
    });

    // 2. Parallax backgrounds
    gsap.utils.toArray('[data-parallax-speed]').forEach(element => {
      const speed = element.dataset.parallaxSpeed || 0.5;
      
      ScrollTrigger.create({
        trigger: element,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
        onUpdate: (self) => {
          const yPos = -(self.progress * 100 * speed);
          gsap.set(element, { yPercent: yPos });
        }
      });
    });

    // 3. Scale-in effects for cards
    gsap.utils.toArray('[data-scale-in]').forEach(element => {
      ScrollTrigger.create({
        trigger: element,
        start: "top 80%",
        end: "top 20%",
        scrub: 1,
        onUpdate: (self) => {
          const scale = 0.8 + (self.progress * 0.2);
          gsap.set(element, { scale });
        }
      });
    });

    // 4. Stagger reveals for grouped elements
    gsap.utils.toArray('[data-reveal-stagger]').forEach(container => {
      const children = container.children;
      
      ScrollTrigger.create({
        trigger: container,
        start: "top 85%",
        onEnter: () => {
          gsap.from(children, {
            y: 40,
            opacity: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: "power2.out"
          });
        }
      });
    });

    // 5. Text reveals with magnetic effect
    gsap.utils.toArray('[data-reveal]').forEach(element => {
      ScrollTrigger.create({
        trigger: element,
        start: "top 90%",
        onEnter: () => {
          gsap.from(element, {
            y: 30,
            opacity: 0,
            duration: 0.8,
            ease: "back.out(1.7)"
          });
        }
      });
    });

    // 6. Progress-based color shifts
    ScrollTrigger.create({
      start: 0,
      end: "max",
      scrub: true,
      onUpdate: (self) => {
        const progress = self.progress;
        const hue = 300 + (progress * 60); // From magenta to purple
        document.documentElement.style.setProperty('--scroll-progress', progress);
        document.documentElement.style.setProperty('--dynamic-hue', hue);
      }
    });

    // 7. Magnetic mouse effects for premium elements
    gsap.utils.toArray('[data-magnetic]').forEach(element => {
      let hover = false;
      
      element.addEventListener('mouseenter', () => {
        hover = true;
        gsap.to(element, {
          scale: 1.1,
          duration: 0.3,
          ease: "power2.out"
        });
      });
      
      element.addEventListener('mouseleave', () => {
        hover = false;
        gsap.to(element, {
          scale: 1,
          x: 0,
          y: 0,
          duration: 0.3,
          ease: "power2.out"
        });
      });
      
      element.addEventListener('mousemove', (e) => {
        if (!hover) return;
        
        const rect = element.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        
        gsap.to(element, {
          x: x * 0.3,
          y: y * 0.3,
          duration: 0.3,
          ease: "power2.out"
        });
      });
    });

    // Cleanup function
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return null; // This component only handles effects
};

export default PremiumScrollEffects;